import { err, ok } from 'neverthrow';
import { type z } from 'zod';

import type {
  ViewLayoutDataModule,
  ViewLayoutModuleContext,
  ViewLayoutModuleMode,
} from './types';
import { isReservedLayoutTypeId } from './types';

type DefineLayoutModuleConfig<Id extends string, S extends z.ZodObject<z.ZodRawShape>> = {
  id: Id;
  schema: S;
  getMode?: (ctx: ViewLayoutModuleContext) => ViewLayoutModuleMode;
  fromDatabase?: (
    raw: Record<string, unknown> | undefined,
    defaults: z.infer<S>
  ) => z.infer<S>;
  toDatabase?: (value: z.infer<S>) => Record<string, unknown>;
  onInvalid?: (issue: { layoutId: Id; error: z.ZodError }) => void;
};

/**
 * Low-ceremony factory for creating a typed {@link ViewLayoutDataModule}.
 *
 * Defaults are the single source of truth in the Zod schema via field-level
 * `.default(...)`. They are derived once with `schema.parse({})`, so adding a
 * new property is a one-line schema change: existing persisted rows missing
 * that key receive the default on read, with no migration.
 *
 * Generates `parseProperties` and the {@link PropertiesStorageConverter}, and
 * validates at module-load that defaults can be derived (throws if a required
 * field lacks a `.default()`).
 *
 * ```ts
 * export const chartLayoutModule = defineLayoutModule({
 *   id: CHART_LAYOUT_ID,
 *   schema: z.object({
 *     chartType: z.enum(['bar', 'line', 'pie']).default('bar'),
 *     xAxisFieldId: z.string().default(''),
 *   }),
 * });
 * ```
 *
 * For non-additive migrations (rename, derive-from-legacy) or write
 * normalization, pass the optional `fromDatabase` / `toDatabase` overrides.
 */
export function defineLayoutModule<
  Id extends string,
  S extends z.ZodObject<z.ZodRawShape>,
>(config: DefineLayoutModuleConfig<Id, S>): ViewLayoutDataModule<Id, z.infer<S>> {
  type Props = z.infer<S>;

  const {
    id,
    schema,
    getMode = () => 'read-write',
    onInvalid = (issue) => {
      // eslint-disable-next-line no-console
      console.warn(
        `[${issue.layoutId}] Invalid layout properties, using defaults:`,
        issue.error.message
      );
    },
  } = config;

  if (isReservedLayoutTypeId(id)) {
    throw new Error(
      `[${id}] defineLayoutModule: layout id "${id}" is a reserved built-in ` +
        `storage code and cannot be used as a layout-module id.`
    );
  }

  let defaults: Props;

  try {
    defaults = schema.parse({});
  } catch (error) {
    throw new Error(
      `[${id}] defineLayoutModule could not derive defaults from the schema. ` +
        `Every required field must declare a .default(); schema.parse({}) failed: ${
          error instanceof Error ? error.message : String(error)
        }`
    );
  }

  const fromDatabase =
    config.fromDatabase ??
    ((raw: Record<string, unknown> | undefined, defs: Props): Props => {
      const result = schema.safeParse(raw ?? {});

      if (result.success) {
        return result.data;
      }

      onInvalid({ layoutId: id, error: result.error });

      return defs;
    });

  const toDatabase =
    config.toDatabase ?? ((value: Props): Record<string, unknown> => value);

  return {
    id,
    schema,
    getMode,

    converter: {
      defaults,
      fromDatabase(stored) {
        return fromDatabase(stored, defaults);
      },
      toDatabase,
    },

    parseProperties(properties: unknown) {
      const result = schema.safeParse(properties);

      if (result.success) {
        return ok(result.data);
      }

      return err({
        layoutId: id,
        reason: 'VIEW_LAYOUT_INVALID_PROPERTIES',
        message: result.error.message,
      });
    },
  };
}
